<template>
  <v-dialog
    v-model="internalVisible"
    :max-width="modalSize"
    persistent
  >
    <v-card
      :class="modalColor"
      class="rounded-xl overflow-hidden"
    >
      <v-card-text class="pt-0 bg-white overflow-y-auto">
        <div class="flex justify-end">
          <v-btn
            density="compact"
            color="midGray"
            class="pt-5"
            icon="fas fa-times"
            variant="text"
            @click="closeModal"
          ></v-btn>
        </div>

        <!-- icono del modal -->
        <section
          v-if="$slots['title-icon']"
          class="mb-4 flex justify-center"
        >
          <div class="relative">
            <!-- Badge principal con color de marca -->
            <div
              class="w-14 h-14 rounded-2xl flex items-center justify-center"
              :class="iconBackgroundClass"
            >
              <v-icon size="26">
                <slot name="title-icon" />
              </v-icon>
            </div>
          </div>
        </section>
        <!-- Fin icono del modal -->

        <!-- Título del modal -->
        <div
          v-if="$slots['title']"
          class="bold-font text-center mb-4"
        >
          <h5 class="bold-font text-gray-dark text-xl">
            <slot name="title" />
          </h5>
        </div>
        <!-- Fin Título del modal -->

        <!-- Sub Título del modal -->
        <p
          v-if="$slots['sub-title']"
          class="text-center regular-font text-sm md:text-base mb-0"
        >
          <slot name="sub-title" />
        </p>
        <!-- Fin Sub Título del modal -->

        <!-- Contenido Principal -->
        <section class="mt-8">
          <slot></slot>
        </section>
        <!-- Fin Contenido Principal -->

        <!-- Contenido de los botones de acción -->
        <section
          v-if="showActionButtons"
          class="mt-8"
        >
          <div
            v-if="$slots['action-buttons']"
            class="flex flex-col md:flex-row justify-center md:justify-end gap-4"
          >
            <!-- Acciones principales (slot) primero en móvil, después en desktop -->
            <div class="order-first md:order-last w-full md:w-auto">
              <slot name="action-buttons" />
            </div>
            <!-- Botón Cancelar después en móvil, primero en desktop -->
            <v-btn
              class="mr-0 md:mr-3 mb-2 md:mb-0 w-full md:w-auto order-last md:order-first"
              color="blackFlat"
              @click="closeModal"
              size="large"
              variant="outlined"
            >
              Cancelar
            </v-btn>
          </div>

          <div
            v-else
            class="flex flex-col md:flex-row justify-center md:justify-end gap-4"
          >
            <v-btn
              class="w-full md:w-auto"
              color="blackFlat"
              @click="closeModal"
              size="large"
              variant="outlined"
            >
              Cerrar
            </v-btn>
          </div>
        </section>

        <!-- Contenido después de los botones de acción -->
        <div
          v-if="$slots['after-action-buttons']"
          class="text-right mt-4"
        >
          <slot name="after-action-buttons" />
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

  interface Props {
    visible: boolean;
    color: string;
    size: ModalSize;
    showActionButtons?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    color: 'blue',
    size: 'md',
    showActionButtons: true,
  });

  const internalVisible = computed({
    get: () => props.visible,
    set: (val: boolean) => emit('update:visible', val),
  });

  const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void;
  }>();

  const modalColor = computed(() => `${props.color}-modal`);

  /**
   * @author Omar Sánchez Martínez
   * @since 13/08/2025
   * @description Maps the provided modal color to Tailwind background color utility classes for the icon.
   */
  const mapColorToClasses = (color: string): { background: string } => {
    switch (color) {
      case 'blue':
        return { background: 'bg-blue' };
      case 'red':
        return { background: 'bg-red' };
      case 'yellow':
        return { background: 'bg-yellow' };
      case 'darkYellow':
        return { background: 'bg-yellow-dark' };
      case 'teal':
        return { background: 'bg-teal' };
      case 'orange':
        return { background: 'bg-orange' };
      case 'purple':
        return { background: 'bg-purple' };
      case 'darkGreen':
        return { background: 'bg-darkGreen' };
      default:
        return { background: 'bg-teal' };
    }
  };

  const iconBackgroundClass = computed(() => mapColorToClasses(props.color).background);

  const modalSize = computed(() => {
    switch (props.size) {
      case 'xs':
        return '300';
      case 'sm':
        return '400px';
      case 'md':
        return '550px';
      case 'lg':
        return '700px';
      case 'xl':
        return '900px';
      case 'xxl':
        return '1150px';
      default:
        return '550px';
    }
  });

  const closeModal = () => {
    emit('update:visible', false);
  };
</script>

<style scoped lang="scss">
  /* Ensure rounded corners apply to the teleported dialog content */
  :deep(.v-overlay__content .v-card) {
    border-radius: 24px !important; /* ~ rounded-3xl */
    overflow: hidden; /* clip inner content to the rounded shape */
  }

  /* Optionally round the overlay content itself to avoid any subtle edges */
  :deep(.v-overlay__content) {
    border-radius: 24px !important;
  }
</style>
